import React, { useState, useEffect, useRef } from "react";
import { useProduct } from "../../contexts/ProductContext";

const MagazineProductTile = ({
  product,
  size = "medium",
  searchTerm,
}) => {
  const { openProduct } = useProduct();
  const containerRef = useRef(null);
  const [isHorizontalLayout, setIsHorizontalLayout] = useState(false);

  // Use ResizeObserver to monitor component size and determine best layout
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        const aspectRatio = width / height;
        // Use horizontal layout if component is wide enough
        setIsHorizontalLayout(aspectRatio >= 1.6);
      }
    });

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, []);

  const highlightMatch = (text, search) => {
    if (!search || !text) return text;

    const parts = text.split(new RegExp(`(${search})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === search.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 px-0.5">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const handleClick = (e) => {
    e.preventDefault();
    openProduct(product.id);
  };

  const getTileClasses = () => {
    let classes =
      "relative border magazine-tile cursor-pointer bg-white rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300 w-full h-full";

    // Size classes
    if (size === "small") {
      classes += " magazine-tile-small";
    } else if (size === "large-vertical") {
      classes += " magazine-tile-large-vertical";
    } else if (size === "large-square") {
      classes += " magazine-tile-large-square";
    } else {
      classes += " magazine-tile-medium";
    }

    return classes;
  };

  // Select appropriate image based on size
  // Use API-provided sizes: thumbnail (100px), small (315px), large (660px), xlarge (1024px)
  const getImageSources = () => {
    let imageObj;
    
    if (size === "small") {
      // For small tiles, use 660px (large)
      imageObj = product.imageSm;
    } else if (size === "large-vertical" || size === "large-square") {
      // For large tiles, use the xlarge size (1024px) for best quality
      imageObj = product.imageXl || product.imageLg;
    } else {
      // For medium/normal tiles, use 660px (large)
      imageObj = product.imageMd;
    }
    
    // Return both webp and jpg sources, or fallback to default image
    if (imageObj && typeof imageObj === 'object' && (imageObj.webp || imageObj.jpg)) {
      return {
        webp: imageObj.webp || product.image,
        jpg: imageObj.jpg || product.image
      };
    }
    
    // Fallback for old format or missing data
    return {
      webp: product.image,
      jpg: product.image
    };
  };

  const imageSources = getImageSources();
  
  // Render horizontal layout for wide components
  if (isHorizontalLayout) {
    return (
      <div ref={containerRef} onClick={handleClick} className={`${getTileClasses()} magazine-tile-horizontal`}>
        <div className="flex h-full">
          {/* Image Section - Left side */}
          <div className="w-1/2 bg-white flex items-center justify-center p-2 overflow-hidden">
            <picture className="w-full h-full flex items-center justify-center">
              <source 
                srcSet={imageSources.webp} 
                type="image/webp"
              />
              <source 
                srcSet={imageSources.jpg} 
                type="image/jpeg"
              />
              <img
                src={imageSources.jpg}
                alt={product.name}
                className="w-full h-full object-contain transition-transform duration-300 hover:scale-110"
                loading="lazy"
              />
            </picture>
          </div>

          {/* Content Section - Right side */}
          <div className="w-1/2 flex flex-col justify-center p-3 bg-white border-l border-gray-100">
            {/* SKU */}
            <div className="text-xs font-mono text-gray-400 leading-tight">
              {highlightMatch(product.sku || `#${product.id}`, searchTerm)}
            </div>

            {/* Name */}
            <h3
              className={`font-light text-bmb-text leading-tight line-clamp-2 my-1 ${
                size === "small"
                  ? "text-sm"
                  : size.includes("large")
                  ? "text-base"
                  : "text-sm"
              }`}
            >
              {highlightMatch(product.name, searchTerm)}
            </h3>

            {/* Options (Material) */}
            {product.material && (
              <p className="text-xs text-gray-500 leading-tight line-clamp-1">
                {product.material}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Default vertical layout
  return (
    <div ref={containerRef} onClick={handleClick} className={getTileClasses()}>
      {/* Image Section - Absolute positioned to fill space above text */}
      <div className="absolute inset-0 bottom-24 bg-white flex items-center justify-center p-2 overflow-hidden">
        <picture className="w-full h-full flex items-center justify-center">
          <source 
            srcSet={imageSources.webp} 
            type="image/webp"
          />
          <source 
            srcSet={imageSources.jpg} 
            type="image/jpeg"
          />
          <img
            src={imageSources.jpg}
            alt={product.name}
            className="w-full h-full object-contain transition-transform duration-300 hover:scale-110"
            loading="lazy"
          />
        </picture>
      </div>

      {/* Content Section - Fixed at bottom with reserved space */}
      <div className="flex flex-col justify-center absolute bottom-0 left-0 right-0 h-24 p-3 bg-white border-t border-gray-100">
        {/* SKU */}
        <div className="text-xs font-mono text-gray-400 leading-tight">
          {highlightMatch(product.sku || `#${product.id}`, searchTerm)}
        </div>

        {/* Name */}
        <h3
          className={`font-light text-bmb-text leading-tight line-clamp-2 my-1 ${
            size === "small"
              ? "text-sm"
              : size.includes("large")
              ? "text-base"
              : "text-sm"
          }`}
        >
          {highlightMatch(product.name, searchTerm)}
        </h3>

        {/* Options (Material) */}
        {product.material && (
          <p className="text-xs text-gray-500 leading-tight line-clamp-1">
            {product.material}
          </p>
        )}
      </div>
    </div>
  );
};

export default MagazineProductTile;
